import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, AlertTriangle, ExternalLink, History, ArrowRight,
  CheckCircle2, XCircle, Loader2, Lock,
} from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { isAdmin, canWrite } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// ----------------------------------------------------------------------
// Publish-to-GFMP panel rendered inside TrackEditor's "GFMP Publish" tab.
//
// Strict contract:
//   * No private rights data is ever sent to the public catalogue from here.
//   * Public fields are explicit, length-capped, and stored in their own
//     subdocument on the track.
//   * Executive transitions (Approved, Published, Removed) require role
//     super_admin — surface still renders, but action buttons disable for
//     non-executives with a clear inline tooltip.
//   * Every status change calls /api/tracks/{id}/gfmp/transition; backend
//     enforces the full state-machine + audit log.
// ----------------------------------------------------------------------

const PUBLISH_STATUS_LABEL_COLOUR = {
  "Draft":                                  { bg: "hsl(225 14% 92%)", fg: "hsl(225 14% 22%)" },
  "Internal Review":                        { bg: "hsl(40 70% 92%)",  fg: "hsl(38 70% 28%)" },
  "Blocked":                                { bg: "hsl(0 60% 92%)",   fg: "hsl(0 60% 30%)" },
  "Ready for Executive Review":             { bg: "hsl(220 80% 92%)", fg: "hsl(220 80% 28%)" },
  "Approved for GFMP Sync Catalogue":       { bg: "hsl(140 38% 90%)", fg: "hsl(140 50% 22%)" },
  "Published":                              { bg: "hsl(347 50% 90%)", fg: "hsl(347 70% 22%)" },
  "Removed from Public Catalogue":          { bg: "hsl(20 30% 90%)",  fg: "hsl(20 30% 28%)" },
};

function StatusBadge({ status }) {
  const s = PUBLISH_STATUS_LABEL_COLOUR[status] || PUBLISH_STATUS_LABEL_COLOUR["Draft"];
  return (
    <Badge
      variant="outline"
      style={{ background: s.bg, color: s.fg, borderColor: s.fg + "33" }}
      className="text-[10px] uppercase tracking-wide font-medium whitespace-nowrap"
      data-testid="publish-status-badge"
    >
      {status}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function PublishToGFMPPanel({ trackId, user, onChanged }) {
  const writer = canWrite(user);
  const executive = isAdmin(user);

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [confirmTransition, setConfirmTransition] = useState(null); // { to_status, note, label }

  // Local form mirrors for the editor inputs (so typing doesn't refetch).
  const [pf, setPf] = useState({});
  const [checks, setChecks] = useState({});
  const [moodInput, setMoodInput] = useState("");

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tracks/${trackId}/gfmp`);
      setState(r.data);
      setPf({ ...(r.data?.publish?.public_fields || {}) });
      setChecks({ ...(r.data?.publish?.checks || {}) });
      setMoodInput(((r.data?.publish?.public_fields?.public_mood_tags) || []).join(", "));
    } catch (e) {
      toast.error(errMsg(e, "Could not load GFMP publish state."));
    } finally {
      setLoading(false);
    }
  }, [trackId]);

  useEffect(() => {
    let cancelled = false;
    if (!trackId) return;
    loadState();
    api.get("/gfmp/meta")
      .then((r) => { if (!cancelled) setMeta(r.data); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [trackId, loadState]);

  const publish = state?.publish;
  const checkKeys = useMemo(
    () => state?.check_keys || meta?.publish_check_keys || {},
    [state?.check_keys, meta?.publish_check_keys],
  );
  const checkGroups = useMemo(() => {
    const groups = {};
    Object.entries(checkKeys).forEach(([k, v]) => {
      const g = v?.group || "Other";
      if (!groups[g]) groups[g] = [];
      groups[g].push({ key: k, label: v?.label || k });
    });
    return groups;
  }, [checkKeys]);

  const allowedTransitions = state?.allowed_transitions || [];
  const execRequired = new Set(state?.executive_required_for || []);

  const publishLocked = publish?.status === "Published";
  const publicTitleSet = (pf?.public_title || "").trim().length > 0;
  const allChecksTrue = Object.keys(checkKeys).length > 0 && Object.keys(checkKeys).every((k) => !!checks[k]);

  function setPfField(k, v) {
    setPf((s) => ({ ...s, [k]: v }));
  }

  function setCheckKey(k, v) {
    setChecks((s) => ({ ...s, [k]: !!v }));
  }

  async function savePublicFields() {
    if (publishLocked) {
      toast.error("Move the record out of Published before editing public fields.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...pf,
        public_mood_tags: (moodInput || "")
          .split(/[,;]/)
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const r = await api.put(`/tracks/${trackId}/gfmp/public-fields`, payload);
      setState((s) => ({ ...(s || {}), publish: r.data.publish }));
      setPf({ ...(r.data?.publish?.public_fields || {}) });
      toast.success("Approved public metadata saved.");
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Could not save public fields."));
    } finally {
      setSaving(false);
    }
  }

  async function saveChecks() {
    setSaving(true);
    try {
      const r = await api.put(`/tracks/${trackId}/gfmp/checks`, { checks });
      setState((s) => ({ ...(s || {}), publish: r.data.publish }));
      setChecks({ ...(r.data?.publish?.checks || {}) });
      toast.success("Readiness checklist saved.");
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Could not save checklist."));
    } finally {
      setSaving(false);
    }
  }

  async function transitionTo(target, note = "") {
    setSaving(true);
    try {
      const r = await api.post(`/tracks/${trackId}/gfmp/transition`, { to_status: target, note });
      setState((s) => ({ ...(s || {}), publish: r.data.publish, allowed_transitions: undefined }));
      toast.success(`Status changed → ${target}`);
      await loadState();
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.missing_checks) {
        toast.error("Complete the readiness checklist first.");
      } else {
        toast.error(errMsg(e, "Could not change status."));
      }
    } finally {
      setSaving(false);
    }
  }

  function requestTransition(target) {
    const needsExec = execRequired.has(target);
    if (needsExec && !executive) {
      toast.error("This transition requires an authorised executive (super_admin).");
      return;
    }
    setConfirmTransition({ to_status: target, note: "", label: target, exec: needsExec });
  }

  if (!trackId) {
    return (
      <Alert data-testid="publish-needs-save">
        <AlertTriangle className="w-4 h-4" />
        <AlertDescription>Save this track once before configuring GFMP publishing.</AlertDescription>
      </Alert>
    );
  }
  if (!writer) {
    return (
      <Alert data-testid="publish-readonly-banner">
        <Lock className="w-4 h-4" />
        <AlertDescription>
          GFMP publish controls are restricted to Catalog Manager, GFMP Reviewer, and Super Admin.
        </AlertDescription>
      </Alert>
    );
  }
  if (loading || !publish) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8" data-testid="publish-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading GFMP publish state…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="publish-to-gfmp-panel">
      {/* Header bar */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" style={{ color: "hsl(347 60% 22%)" }} />
              Publish to GFMP Sync Catalogue
            </CardTitle>
            <CardDescription>
              Approved public metadata + readiness checklist + state-machine controlled visibility.
              Executive (super_admin) authority is required for Approved, Published, and Removed.
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={publish.status} />
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Updated {fmtDate(publish.last_updated_at)}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {publish.status === "Published" && (
            <Alert variant="default" className="border-primary/40" data-testid="publish-live-notice">
              <CheckCircle2 className="w-4 h-4" />
              <AlertDescription>
                This record is <strong>live</strong> on the public GFMP Sync Catalogue.
                {publish.published_at && <> Published {fmtDate(publish.published_at)}.</>}
                <Link
                  to="/gfmp/sync-catalogue"
                  className="ml-2 underline inline-flex items-center gap-1"
                  data-testid="publish-open-public"
                  target="_blank"
                  rel="noreferrer"
                >
                  View on public catalogue <ExternalLink className="w-3 h-3" />
                </Link>
              </AlertDescription>
            </Alert>
          )}
          {publish.status === "Removed from Public Catalogue" && (
            <Alert variant="destructive" data-testid="publish-removed-notice">
              <XCircle className="w-4 h-4" />
              <AlertDescription>
                This record has been removed from the public catalogue.
                {publish.removed_at && <> Removed {fmtDate(publish.removed_at)}.</>}
              </AlertDescription>
            </Alert>
          )}
          {publish.status === "Blocked" && (
            <Alert variant="destructive" data-testid="publish-blocked-notice">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                This record is currently blocked from public publication. Resolve the blocking
                condition and route it back through Internal Review.
              </AlertDescription>
            </Alert>
          )}

          {/* Transition action row */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mr-1">
              Move to:
            </div>
            {allowedTransitions.length === 0 && (
              <span className="text-[12px] text-muted-foreground italic">
                No further transitions available from this status.
              </span>
            )}
            {allowedTransitions.map((t) => {
              const needsExec = execRequired.has(t);
              const disabled = saving || (needsExec && !executive);
              return (
                <Button
                  key={t}
                  size="sm"
                  variant={t === "Published" ? "default" : "outline"}
                  onClick={() => requestTransition(t)}
                  disabled={disabled}
                  data-testid={`publish-transition-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={needsExec ? "border-primary/40" : undefined}
                  title={needsExec && !executive
                    ? "Requires authorised executive (super_admin)"
                    : ""}
                >
                  {needsExec && <Lock className="w-3 h-3 mr-1" />}
                  {t} <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Approved public fields */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Approved public metadata</CardTitle>
          <CardDescription>
            ONLY these fields appear on the public GFMP Sync Catalogue. Private rights data
            (writers, splits, publishers, contracts, internal notes) is never exposed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pf-title">Public title *</Label>
              <Input
                id="pf-title"
                value={pf.public_title || ""}
                onChange={(e) => setPfField("public_title", e.target.value)}
                maxLength={200}
                disabled={publishLocked || saving}
                data-testid="publish-public-title"
              />
            </div>
            <div>
              <Label htmlFor="pf-artist">Public artist / project name</Label>
              <Input
                id="pf-artist"
                value={pf.public_artist_project || ""}
                onChange={(e) => setPfField("public_artist_project", e.target.value)}
                maxLength={200}
                disabled={publishLocked || saving}
                data-testid="publish-public-artist"
              />
            </div>
            <div>
              <Label htmlFor="pf-genre">Public genre</Label>
              <Input
                id="pf-genre"
                value={pf.public_genre || ""}
                onChange={(e) => setPfField("public_genre", e.target.value)}
                maxLength={100}
                disabled={publishLocked || saving}
                data-testid="publish-public-genre"
              />
            </div>
            <div>
              <Label htmlFor="pf-moods">Public mood / descriptive tags</Label>
              <Input
                id="pf-moods"
                value={moodInput}
                onChange={(e) => setMoodInput(e.target.value)}
                placeholder="e.g. cinematic, hopeful, dark"
                disabled={publishLocked || saving}
                data-testid="publish-public-moods"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Separate with commas.</p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="pf-desc">Public description</Label>
              <Textarea
                id="pf-desc"
                value={pf.public_description || ""}
                onChange={(e) => setPfField("public_description", e.target.value)}
                rows={3}
                maxLength={1200}
                disabled={publishLocked || saving}
                data-testid="publish-public-description"
              />
            </div>
          </div>

          <Separator />

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="publish-availability-grid">
            <SwitchRow
              testId="publish-toggle-instrumental"
              label="Instrumental version available"
              value={!!pf.availability_instrumental}
              onChange={(v) => setPfField("availability_instrumental", v)}
              disabled={publishLocked || saving}
            />
            <SwitchRow
              testId="publish-toggle-clean"
              label="Clean version available"
              value={!!pf.availability_clean}
              onChange={(v) => setPfField("availability_clean", v)}
              disabled={publishLocked || saving}
            />
            <SwitchRow
              testId="publish-toggle-onestop"
              label="One-stop / controlled-rights indicator"
              hint="Only enable if manually approved by an authorised executive."
              value={!!pf.one_stop_or_controlled}
              onChange={(v) => setPfField("one_stop_or_controlled", v)}
              disabled={publishLocked || saving}
            />
            <SwitchRow
              testId="publish-toggle-preview"
              label="Stream-only preview prepared on request"
              value={!!pf.public_preview_available}
              onChange={(v) => setPfField("public_preview_available", v)}
              disabled={publishLocked || saving}
            />
            <SwitchRow
              testId="publish-toggle-artwork"
              label="Use generic artwork placeholder"
              hint="Approved artwork upload remains a future phase — placeholder is fine for now."
              value={!!pf.public_artwork_placeholder}
              onChange={(v) => setPfField("public_artwork_placeholder", v)}
              disabled={publishLocked || saving}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadState}
              disabled={saving}
              data-testid="publish-refresh"
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={savePublicFields}
              disabled={publishLocked || saving}
              data-testid="publish-save-public-fields"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Save public metadata
            </Button>
          </div>
          {!publicTitleSet && (
            <Alert variant="destructive" className="mt-1" data-testid="publish-title-missing">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                A public title is required before moving past Internal Review.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Readiness checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Readiness checklist</CardTitle>
          <CardDescription>
            All checks must be confirmed before this record can be moved to Ready for Executive
            Review, Approved, or Published.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(checkGroups).map(([group, items]) => (
            <div key={group}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                {group}
              </div>
              <ul className="grid sm:grid-cols-2 gap-2">
                {items.map(({ key, label }) => (
                  <li
                    key={key}
                    className="flex items-start gap-2 rounded-md border p-2.5"
                    data-testid={`publish-check-${key}`}
                  >
                    <Switch
                      checked={!!checks[key]}
                      onCheckedChange={(v) => setCheckKey(key, v)}
                      disabled={saving}
                      aria-label={label}
                      data-testid={`publish-check-switch-${key}`}
                    />
                    <Label
                      className="text-[12.5px] leading-snug cursor-pointer flex-1"
                      onClick={() => !saving && setCheckKey(key, !checks[key])}
                    >
                      {label}
                    </Label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground" data-testid="publish-checks-summary">
              {Object.values(checks).filter(Boolean).length}/{Object.keys(checkKeys).length} confirmed
              {allChecksTrue && <span className="ml-2 text-emerald-700">· ready</span>}
            </div>
            <Button size="sm" onClick={saveChecks} disabled={saving} data-testid="publish-save-checks">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Save checklist
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4" /> Publish history
          </CardTitle>
          <CardDescription>
            Every status change is recorded here and additionally written to the immutable Audit Log.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!publish.history?.length ? (
            <div className="text-[12px] text-muted-foreground italic" data-testid="publish-history-empty">
              No transitions yet. The first move will appear here.
            </div>
          ) : (
            <ol className="space-y-2 text-[12.5px]" data-testid="publish-history-list">
              {[...publish.history].reverse().map((h, i) => (
                <li key={i} className="flex items-start gap-3 rounded-md border p-2.5">
                  <div className="font-mono text-[10.5px] text-muted-foreground whitespace-nowrap">
                    {fmtDate(h.ts)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge status={h.from_status} />
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <StatusBadge status={h.to_status} />
                    </div>
                    <div className="text-muted-foreground text-[11px] mt-1">
                      by <span className="font-medium text-foreground">{h.user_name || h.user_email}</span>
                      {h.note && <span> — {h.note}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog for transitions */}
      <AlertDialog
        open={!!confirmTransition}
        onOpenChange={(o) => { if (!o) setConfirmTransition(null); }}
      >
        <AlertDialogContent data-testid="publish-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move record to <span className="font-mono">{confirmTransition?.label}</span>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTransition?.exec
                ? "This is an executive transition that changes the record's public visibility."
                : "This will record an audit entry and update the publish history."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="transition-note">Internal note (optional)</Label>
            <Textarea
              id="transition-note"
              rows={3}
              value={confirmTransition?.note || ""}
              onChange={(e) =>
                setConfirmTransition((c) => (c ? { ...c, note: e.target.value } : c))
              }
              maxLength={500}
              placeholder="Anything notable about this transition (visible in publish history)…"
              data-testid="publish-transition-note"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const t = confirmTransition;
                setConfirmTransition(null);
                if (t) await transitionTo(t.to_status, t.note);
              }}
              data-testid="publish-confirm-go"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SwitchRow({ label, value, onChange, disabled, hint, testId }) {
  return (
    <div className="flex items-start gap-3 rounded-md border p-2.5">
      <Switch
        checked={value}
        onCheckedChange={onChange}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
      />
      <div className="flex-1 min-w-0">
        <Label className="text-[12.5px] leading-snug">{label}</Label>
        {hint && <p className="text-[10.5px] text-muted-foreground mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}
